// The Cupola: the view from inside the ISS's observation module, looking
// down at the Earth through its seven windows. Entered from the ISS card's
// "Cupola view" button, or with C, whenever the station is the focused body
// (Track the ISS, /viewer/#iss, the planet nav, after a lesson); left with
// Esc, C or the Leave button, which puts the previous framing back.
//
// The camera rides the station's own SGP4 state (the model main.js places
// every frame): at its position, in its local vertical, local horizontal
// frame, so the Earth stays below and the velocity at the top of the screen.
// The scaled scene renders as for any ISS close view (the satellite
// inspection origin shift, the close Earth's imagery, clouds, night lights
// and aurora at 420 km); the Cupola itself is a second scene in metres,
// drawn by its own RenderPass right after the scene passes with its own
// depth buffer, so it can never z-fight the Earth 400 km beyond it.
//
// What the module is built from (quoted from the sources):
//   ESA, "Cupola": "Six trapezoidal side windows and a circular top window
//     80 cm in diameter, making it the largest window ever flown in space."
//     "Overall height: 1500 mm", "Maximum diameter: 2955 mm" (including the
//     debris protection with the shutters closed). Each window has "an inner
//     scratch pane", "two 25 mm-thick pressure panes" and "a debris pane on
//     the outside"; "external shutters, which can be opened by the crew
//     inside with the simple turn of a wrist"; the inside is "dominated by
//     upper and lower handrails around the cabin" and "close-out panels".
//     https://www.esa.int/Science_Exploration/Human_and_Robotic_Exploration/Node-3_Cupola/Cupola
//   P. Deloo (ESA ISS infrastructure division), "The ISS Cupola, a unique
//     location for the accommodation of scientific observation instruments",
//     7 December 2009: "The Cupola is installed on the Nadir side of the
//     station, hence directly facing the earth." "The side windows allow the
//     observation of the atmosphere limb." Top window "Diameter (transparent
//     area): 27.8 in. (70.6 cm)", "Overall thickness 8.18 in. (20.8 cm)".
//     Side windows (transparent area) "Height 40.5 cm", "Short side length
//     40.0 cm", "Long side length 64.4 cm", "Overall thickness 18.50 cm",
//     "Corner radius 3.24 cm". "2 standard ISS seat tracks on each of the 6
//     mullions"; "Continuous handrail at the bottom of the side windows over
//     the complete element circumference (upper handrail)", cross-section
//     0.754 x 1.375 in.
//     http://esamultimedia.esa.int/docs/hsf_research/Climate_change_ISS_presentations/Cupola_Deloo.pdf
//   eoPortal (ESA): "Cupola was berthed on Tranquility's nadir (Earth-facing)
//     port." https://eoportal.org/satellite-missions/iss-cupola
//   NASA, "Cupola": "Its six side windows and a direct nadir viewing window
//     provide spectacular views of Earth"; height 4.7 feet, diameter 9.8
//     feet; "the robotic workstation that controls the Canadarm2".
//     https://www.nasa.gov/international-space-station/cupola
//   NASA Glenn, PIMS ISS Acceleration Handbook, "+XVV/+ZLV Torque Equilibrium
//     Attitude": "+XVV / +ZLV indicates that the ISS positive X-axis is
//     toward the velocity vector, the Z-axis is towards nadir", held
//     "relative to the Local Vertical Local Horizontal (LVLH)".
//     https://gipoc.grc.nasa.gov/pims/pimsdocs/public/ISS%20Handbook/hb_qs_vehicle_Torque_Equilibrium_Attitude_(TEA).pdf
//   NASA Science 3D Resources, "International Space Station (ISS) (E)
//     (Internal)": the whole pressurised station's inside, modelled by NASA.
//     https://science.nasa.gov/3d-resources/international-space-station-iss-e-internal/
//     Its Cupola and Tranquility round it are what is drawn here
//     (public/models/cupola-nasa.glb, scripts/cupola/build-cupola-model.py),
//     in the station's own axes (the Lab forward of Unity, Columbus to
//     starboard, Kibo to port, BEAM aft of Tranquility, as on orbit). Its
//     glass is the window sizes and places below: the top window 72.1 cm
//     across, the side windows 39.6 cm along the short side, 61.7 along the
//     long and 42.7 up the slope (within 2 to 4% of Deloo's clear glass),
//     tilted 60 degrees from the top window's plane, either side of the
//     velocity at 30 degrees and every 60 degrees round from there, the
//     robotic workstation on the forward side, the inside 2.07 m across and
//     1.27 m from the top window up to Tranquility.
// The windows' thicknesses are Deloo's. The drawn module below is the model's
// stand-in until it loads (or if it cannot); its frame margins, the 1.27 m
// square hatch and the eye's place in the dome are estimates.
import * as THREE from 'three';
import { RenderPass } from 'three/addons/postprocessing/RenderPass.js';
import { issState } from '../iss/orbit.js';
import { modeEnter, modeExit } from '../analytics.js';

const DEG = Math.PI / 180;
const EARTH_METRES = 6371000;

// The module, in metres. Its frame: +z the local vertical (into the
// station), +x the velocity, +y the orbit normal; the top window's inner
// pane lies in z = 0 and the dome opens upward from it.
const TOP_R = 0.3605;           // top window, glass radius (NASA's model)
const TOP_FRAME_R = 0.40;       // the 80 cm pane and its retaining ring (ESA)
const TOP_DEPTH = 0.208;        // top window overall thickness (Deloo)
const SIDE_H = 0.4267;          // side window glass, up the slope (NASA's model)
const SIDE_SHORT = 0.3956;      // short side (NASA's model)
const SIDE_LONG = 0.6169;       // long side (NASA's model)
const SIDE_CORNER = 0.0324;     // corner radius (Deloo)
const SIDE_DEPTH = 0.185;       // side window overall thickness (Deloo)
// The window wells widen outward through that thickness (estimate): with
// straight walls an 18.5 cm well at the clear-glass size would hide the
// limb the side windows are there to show.
const SIDE_FLARE = 0.06;
const TOP_FLARE = 0.05;
// The glass's lower edge is 48.35 cm out and 9.8 cm up in the model; the
// drawn frame's hexagon runs through the line it slopes up from.
const FACET_TILT = 60 * DEG;    // NASA's model
const APERTURE_V0 = 0.1128;     // frame below the glass, along the slope
const HEX_APOTHEM = 0.4835 - APERTURE_V0 * Math.cos(FACET_TILT);
const FACET_LEN = 0.63;         // glass plus frame margins along the slope (estimate)
const WALL_R = 1.035;           // NASA's model
const CEILING_Z = 1.268;        // NASA's model
const HATCH_HALF = 0.62;        // about a 1.27 m square passage (estimate)
const HATCH_TOP = 1.80;
const FACET_COS = Math.cos(FACET_TILT);
const FACET_SIN = Math.sin(FACET_TILT);
const RING_APOTHEM = HEX_APOTHEM + FACET_LEN * FACET_COS;
const RING_Z = FACET_LEN * FACET_SIN;
// The side windows' far (upper) glass edge: the highest a gaze can reach
// and still look out.
const APERTURE_TOP_V = APERTURE_V0 + SIDE_H;
const APERTURE_TOP_R = HEX_APOTHEM + APERTURE_TOP_V * FACET_COS;
const APERTURE_TOP_Z = APERTURE_TOP_V * FACET_SIN;
// Window azimuths (NASA's model): 30 degrees either side of the velocity,
// then every 60 degrees round.
const SIDE_AZIMUTHS = [0, 1, 2, 3, 4, 5].map((k) => (30 + k * 60) * DEG);

// The head: eyes ahead of a neck pivot (parallax as it turns), leaning down
// and toward the window it looks out of as the gaze comes up to the horizon.
const NECK = 0.09;
const LEAN_DOWN = 0.44;
const LEAN_OUT = 0.39;
const EYE_MIN = 0.30;
const HEAD_MIN = 0.45;
const HEAD_MAX = 1.0;
const HEAD_DEFAULT = 0.8;

// Light, in the scene's linear units: a white card square to the Sun reads
// about as bright as the brightest cloud tops through the window.
const SUN_IRRADIANCE = 4.0;
const EARTH_ALBEDO = 0.3;
const WINDOW_SHARE = 0.45;      // share of the lower hemisphere the windows open
const BOUNCE = 0.35;            // Earthlight returned by the inside to faces that look up
// Node 3's lamps through the hatch. By day they are nothing beside the
// Earthlight; at night the eye (or a camera) adapts to the dark Earth and
// the lit cabin reads as it does in night photographs from the Cupola, so
// their share rises as the ground below goes dark.
const CABIN_DAY = 0.04;
const CABIN_NIGHT = 0.55;
const LAMP_DAY = 0.03;
const LAMP_NIGHT = 0.22;
// NASA's model is the Cupola's black anodised frames (1.7% albedo in its
// colour map): lit by the Earth through the glass or by the cabin at night,
// they sit 7 or 8 stops under the clouds. An eye adapts to the room it is
// in, and the crew's photographs show the frames' bolts, seals and rings
// with the clouds near white (ISS038-E-13587, ISS026-E-20960), so the inside
// is drawn this much brighter than the view out. The drawn stand-in, tuned
// on lighter colours, is drawn as lit.
const MODEL_ADAPT = 8.0;

const ENTER_S = 0.9;
const BLACK_HOLD_S = 0.15;
const FADE_IN_S = 0.75;
const LEAVE_FADE_S = 0.32;
const LEAVE_DOLLY_S = 1.1;

const smooth = (x, a, b) => {
  const t = Math.min(1, Math.max(0, (x - a) / (b - a)));
  return t * t * (3 - 2 * t);
};

// ---------------------------------------------------------------------------
// Geometry helpers (2D outlines, then placed on the dome's faces).

// A convex polygon (counter-clockwise [x, y] pairs) with rounded corners.
function filletPolygon(pts, r, seg = 5) {
  const out = [];
  const n = pts.length;
  for (let i = 0; i < n; i++) {
    const p = pts[i];
    const a = pts[(i + n - 1) % n];
    const b = pts[(i + 1) % n];
    let d1x = a[0] - p[0], d1y = a[1] - p[1];
    let d2x = b[0] - p[0], d2y = b[1] - p[1];
    const l1 = Math.hypot(d1x, d1y), l2 = Math.hypot(d2x, d2y);
    d1x /= l1; d1y /= l1; d2x /= l2; d2y /= l2;
    const ang = Math.acos(Math.max(-1, Math.min(1, d1x * d2x + d1y * d2y)));
    const t = r / Math.tan(ang / 2);
    let bx = d1x + d2x, by = d1y + d2y;
    const bl = Math.hypot(bx, by);
    bx /= bl; by /= bl;
    const h = r / Math.sin(ang / 2);
    const cx = p[0] + bx * h, cy = p[1] + by * h;
    const a0 = Math.atan2(p[1] + d1y * t - cy, p[0] + d1x * t - cx);
    const a1 = Math.atan2(p[1] + d2y * t - cy, p[0] + d2x * t - cx);
    let da = a1 - a0;
    while (da > Math.PI) da -= 2 * Math.PI;
    while (da < -Math.PI) da += 2 * Math.PI;
    for (let k = 0; k <= seg; k++) {
      const ang2 = a0 + (da * k) / seg;
      out.push([cx + Math.cos(ang2) * r, cy + Math.sin(ang2) * r]);
    }
  }
  return out;
}

// Each edge of a convex counter-clockwise polygon moved outward by w.
function offsetConvex(pts, w) {
  const n = pts.length;
  const lines = [];
  for (let i = 0; i < n; i++) {
    const p = pts[i], q = pts[(i + 1) % n];
    const dx = q[0] - p[0], dy = q[1] - p[1];
    const l = Math.hypot(dx, dy);
    const nx = dy / l, ny = -dx / l;
    lines.push({ px: p[0] + nx * w, py: p[1] + ny * w, dx, dy });
  }
  const out = [];
  for (let i = 0; i < n; i++) {
    const A = lines[(i + n - 1) % n], B = lines[i];
    const den = A.dx * B.dy - A.dy * B.dx;
    const t = ((B.px - A.px) * B.dy - (B.py - A.py) * B.dx) / den;
    out.push([A.px + A.dx * t, A.py + A.dy * t]);
  }
  return out;
}

const shapeFrom = (pts) => new THREE.Shape(pts.map(([x, y]) => new THREE.Vector2(x, y)));
const pathFrom = (pts) => new THREE.Path(pts.map(([x, y]) => new THREE.Vector2(x, y)));

function regularPolygon(n, circumradius, startAngle) {
  const pts = [];
  for (let k = 0; k < n; k++) {
    const a = startAngle + (k * 2 * Math.PI) / n;
    pts.push([Math.cos(a) * circumradius, Math.sin(a) * circumradius]);
  }
  return pts;
}

// A wall between two closed outlines of the same point count (already
// placed in 3D): the window wells and the hatch.
function loftWall(loop, loop2) {
  const pos = [];
  const uv = [];
  const n = loop.length;
  let run = 0;
  for (let i = 0; i < n; i++) {
    const a = loop[i], b = loop[(i + 1) % n];
    const a2 = loop2[i], b2 = loop2[(i + 1) % n];
    const step = a.distanceTo(b);
    pos.push(a.x, a.y, a.z, b.x, b.y, b.z, b2.x, b2.y, b2.z, a.x, a.y, a.z, b2.x, b2.y, b2.z, a2.x, a2.y, a2.z);
    uv.push(run, 0, run + step, 0, run + step, 1, run, 0, run + step, 1, run, 1);
    run += step;
  }
  const g = new THREE.BufferGeometry();
  g.setAttribute('position', new THREE.Float32BufferAttribute(pos, 3));
  g.setAttribute('uv', new THREE.Float32BufferAttribute(uv, 2));
  g.computeVertexNormals();
  return g;
}

// ---------------------------------------------------------------------------
// Materials: one small physically based shader for every opaque part, lit
// by the Earth below (through the windows), the cabin above, a lamp in the
// hatch and the Sun, which only lands where its ray left through a window.

const SHARED_UNIFORMS = {
  uSunDir: { value: new THREE.Vector3(0, 0, 1) },
  uSunE: { value: new THREE.Vector3() },
  uEarthE: { value: new THREE.Vector3() },
  uCabinE: { value: new THREE.Vector3() },
  uLampPos: { value: new THREE.Vector3(0, 0, HATCH_TOP - 0.15) },
  uLampI: { value: new THREE.Vector3() },
  uWinO: { value: SIDE_AZIMUTHS.map(() => new THREE.Vector3()) },
  uWinU: { value: SIDE_AZIMUTHS.map(() => new THREE.Vector3()) },
  uWinV: { value: SIDE_AZIMUTHS.map(() => new THREE.Vector3()) },
  uWinN: { value: SIDE_AZIMUTHS.map(() => new THREE.Vector3()) },
  // x the glass height along the slope, y and z its half widths at the
  // short and the long side, w the window's thickness.
  uTrap: { value: new THREE.Vector4(SIDE_H, SIDE_SHORT / 2, SIDE_LONG / 2, SIDE_DEPTH) },
  uTop: { value: new THREE.Vector2(TOP_R, TOP_DEPTH) },
  // x the side wells' outward widening, y the top well's.
  uFlare: { value: new THREE.Vector2(SIDE_FLARE, TOP_FLARE) },
  // Which windows the Sun's direction can get through at all (1 or 0, the
  // six sides then the top), worked out once a frame, so the shader only
  // tests those.
  uWinOn: { value: new Array(7).fill(0) },
  // The inside as seen from the eye (the glass's reflection cube), for
  // what the frames, bolts and fittings reflect: mostly the windows across
  // the dome. Off while that cube is being drawn.
  uEnvCube: { value: null },
  uEnvOn: { value: 0 },
  uAdapt: { value: 1 },
};

// Each side window's frame, for the Sun test here and in the shader: the
// middle of the glass's short side on the inner pane, across it, up the
// slope, and the outward normal.
SIDE_AZIMUTHS.forEach((phi, i) => {
  const out = new THREE.Vector3(Math.cos(phi), Math.sin(phi), 0);
  const V = out.clone().multiplyScalar(FACET_COS).setZ(FACET_SIN);
  SHARED_UNIFORMS.uWinO.value[i].copy(out).multiplyScalar(HEX_APOTHEM).addScaledVector(V, APERTURE_V0);
  SHARED_UNIFORMS.uWinU.value[i].set(-Math.sin(phi), Math.cos(phi), 0);
  SHARED_UNIFORMS.uWinV.value[i].copy(V);
  SHARED_UNIFORMS.uWinN.value[i].copy(out).multiplyScalar(FACET_SIN).setZ(-FACET_COS);
});

const INTERIOR_VERT = /* glsl */`
varying vec3 vPos;
varying vec3 vNrm;
varying vec2 vUv;
void main() {
  mat4 m = modelMatrix;
  #ifdef USE_INSTANCING
  m = modelMatrix * instanceMatrix;
  #endif
  vec4 wp = m * vec4(position, 1.0);
  vPos = wp.xyz;
  vNrm = normalize(mat3(m) * normal);
  vUv = uv;
  gl_Position = projectionMatrix * viewMatrix * wp;
}`;

const WINDOW_GLSL = /* glsl */`
uniform vec3 uWinO[6];
uniform vec3 uWinU[6];
uniform vec3 uWinV[6];
uniform vec3 uWinN[6];
uniform vec4 uTrap;
uniform vec2 uTop;
uniform vec2 uFlare;
uniform float uWinOn[7];
float cupSoft(float d) { return clamp(d / 0.012 + 0.5, 0.0, 1.0); }
// Whether the ray from p along L crosses side window i's plane, offset
// outward by off, inside the opening there (widened by grow). A point
// already past the plane passes.
float cupSide(vec3 p, vec3 L, int i, float off, float grow) {
  vec3 n = uWinN[i];
  vec3 o = uWinO[i] + n * off;
  float ahead = dot(o - p, n);
  if (ahead <= 0.0) return 1.0;
  float dn = dot(L, n);
  if (dn <= 1e-4) return 0.0;
  vec3 h = p + L * (ahead / dn) - o;
  float u = dot(h, uWinU[i]);
  float v = dot(h, uWinV[i]);
  float hw = mix(uTrap.y, uTrap.z, clamp(v / uTrap.x, 0.0, 1.0)) + grow;
  return cupSoft(v + grow) * cupSoft(uTrap.x + grow - v) * cupSoft(hw - abs(u));
}
float cupTop(vec3 p, vec3 L, float z, float r) {
  if (p.z <= z) return 1.0;
  if (L.z >= -1e-4) return 0.0;
  vec2 h = p.xy + L.xy * ((z - p.z) / L.z);
  return cupSoft(r - length(h));
}
float cupThrough(vec3 p, vec3 L) {
  float s = 0.0;
  if (uWinOn[6] > 0.5) s = cupTop(p, L, -0.004, uTop.x) * cupTop(p, L, -uTop.y, uTop.x + uFlare.y);
  for (int i = 0; i < 6; i++) {
    if (uWinOn[i] > 0.5) s = max(s, cupSide(p, L, i, 0.004, 0.0) * cupSide(p, L, i, uTrap.w, uFlare.x));
  }
  return s;
}`;

const INTERIOR_FRAG = /* glsl */`
uniform vec3 uColor;
uniform float uRoughness;
uniform float uMetalness;
uniform vec3 uEmissive;
uniform vec3 uSunDir;
uniform vec3 uSunE;
uniform vec3 uEarthE;
uniform vec3 uCabinE;
uniform vec3 uLampPos;
uniform vec3 uLampI;
uniform samplerCube uEnvCube;
uniform float uEnvOn;
uniform float uAdapt;
#ifdef USE_CUPMAP
uniform sampler2D uMap;
#endif
#ifdef USE_CUPMRMAP
uniform sampler2D uMrMap;
#endif
#ifdef USE_CUPNORMALMAP
uniform sampler2D uNormalMap;
#endif
#ifdef USE_CUPEMISSIVEMAP
uniform sampler2D uEmissiveMap;
#endif
varying vec3 vPos;
varying vec3 vNrm;
varying vec2 vUv;
${WINDOW_GLSL}
const float PI_ = 3.14159265;
#ifdef USE_CUPNORMALMAP
// The map's normal on the surface's frame from the derivatives of the
// position and the texture coordinates (no tangents in the model).
vec3 cupPerturb(vec3 N, vec3 p, vec2 uv) {
  vec3 q0 = dFdx(p), q1 = dFdy(p);
  vec2 st0 = dFdx(uv), st1 = dFdy(uv);
  vec3 q1perp = cross(q1, N), q0perp = cross(N, q0);
  vec3 T = q1perp * st0.x + q0perp * st1.x;
  vec3 B = q1perp * st0.y + q0perp * st1.y;
  float det = max(dot(T, T), dot(B, B));
  float s = det == 0.0 ? 0.0 : inversesqrt(det);
  vec3 m = texture2D(uNormalMap, uv).xyz * 2.0 - 1.0;
  return normalize(T * (m.x * s) + B * (m.y * s) + N * m.z);
}
#endif
vec3 cupBrdf(vec3 N, vec3 V, vec3 L, vec3 diffC, vec3 F0, float rough) {
  vec3 H = normalize(V + L);
  float NoH = max(dot(N, H), 0.0);
  float NoL = max(dot(N, L), 0.0);
  float NoV = max(dot(N, V), 1e-3);
  float VoH = max(dot(V, H), 0.0);
  float a = rough * rough;
  float a2 = a * a;
  float dd = NoH * NoH * (a2 - 1.0) + 1.0;
  float D = a2 / (PI_ * dd * dd);
  float k = (rough + 1.0) * (rough + 1.0) / 8.0;
  float G = (NoV / (NoV * (1.0 - k) + k)) * (NoL / (NoL * (1.0 - k) + k));
  vec3 F = F0 + (1.0 - F0) * pow(1.0 - VoH, 5.0);
  return diffC / PI_ + D * G * F / (4.0 * NoV * NoL + 1e-4);
}
void main() {
  vec3 N = normalize(vNrm);
  vec3 V = normalize(cameraPosition - vPos);
  if (dot(N, V) < 0.0) N = -N;
  #ifdef USE_CUPNORMALMAP
  N = cupPerturb(N, vPos, vUv);
  #endif
  vec3 albedo = uColor;
  #ifdef USE_CUPMAP
  albedo *= texture2D(uMap, vUv).rgb;
  #endif
  float rough = uRoughness;
  float metal = uMetalness;
  #ifdef USE_CUPMRMAP
  vec4 mr = texture2D(uMrMap, vUv);
  rough = clamp(rough * mr.g, 0.04, 1.0);
  metal *= mr.b;
  #endif
  vec3 diffC = albedo * (1.0 - metal);
  vec3 F0 = mix(vec3(0.04), albedo, metal);
  // The Earth below through the windows, the cabin and the inside's own
  // bounce above.
  float up = N.z * 0.5 + 0.5;
  vec3 col = diffC / PI_ * mix(uEarthE, uCabinE, up);
  // A blurred reflection: of the inside from the eye where there is a
  // cube of it, else of the same two halves.
  vec3 R = reflect(-V, N);
  float NoV = max(dot(N, V), 1e-3);
  vec3 Fv = F0 + (max(vec3(1.0 - rough), F0) - F0) * pow(1.0 - NoV, 5.0);
  vec3 env = mix(uEarthE, uCabinE, smoothstep(-0.5, 0.5, R.z)) / PI_;
  if (uEnvOn > 0.5) env = textureLod(uEnvCube, R, rough * 7.0).rgb;
  col += env * Fv * (1.0 - 0.6 * rough);
  // The lamp in the hatch.
  vec3 Ld = uLampPos - vPos;
  float d2 = max(dot(Ld, Ld), 0.04);
  Ld *= inversesqrt(d2);
  col += cupBrdf(N, V, Ld, diffC, F0, rough) * uLampI / d2 * max(dot(N, Ld), 0.0);
  // The Sun, where its ray leaves through a window.
  float ns = max(dot(N, uSunDir), 0.0);
  if (ns > 0.0 && uSunE.r + uSunE.g + uSunE.b > 0.0) {
    float through = cupThrough(vPos + N * 0.003, uSunDir);
    col += cupBrdf(N, V, uSunDir, diffC, F0, rough) * uSunE * ns * through;
  }
  vec3 em = uEmissive;
  #ifdef USE_CUPEMISSIVEMAP
  em *= texture2D(uEmissiveMap, vUv).rgb;
  #endif
  // Adapted to the room, with a shoulder over 0.6 so the Sun's patches on
  // the white laptops and racks stay bright without flooding the bloom.
  col *= uAdapt;
  float peak = max(max(col.r, col.g), col.b);
  if (peak > 0.6) col *= (0.6 + (peak - 0.6) / (1.0 + (peak - 0.6) / 0.8)) / peak;
  gl_FragColor = vec4(col + em, 1.0);
}`;

function interiorMaterial({ color, rough = 0.6, metal = 0, emissive = 0x000000, emissiveGain = 1, map = null, mrMap = null, normalMap = null, emissiveMap = null }) {
  const defines = {};
  if (map) defines.USE_CUPMAP = '';
  if (mrMap) defines.USE_CUPMRMAP = '';
  if (normalMap) defines.USE_CUPNORMALMAP = '';
  if (emissiveMap) defines.USE_CUPEMISSIVEMAP = '';
  const c = new THREE.Color(color);
  const e = new THREE.Color(emissive).multiplyScalar(emissiveGain);
  return new THREE.ShaderMaterial({
    defines,
    uniforms: {
      ...SHARED_UNIFORMS,
      uColor: { value: new THREE.Vector3(c.r, c.g, c.b) },
      uRoughness: { value: rough },
      uMetalness: { value: metal },
      uEmissive: { value: new THREE.Vector3(e.r, e.g, e.b) },
      uMap: { value: map },
      uMrMap: { value: mrMap },
      uNormalMap: { value: normalMap },
      uEmissiveMap: { value: emissiveMap },
    },
    vertexShader: INTERIOR_VERT,
    fragmentShader: INTERIOR_FRAG,
    side: THREE.DoubleSide,
  });
}

// The glass: what is behind it (the scaled scene, already drawn) through
// four panes of anti-reflection coated silica, plus a Fresnel reflection of
// the inside, from a small cube rendered at the eye now and then.
const GLASS_VERT = /* glsl */`
varying vec3 vPos;
varying vec3 vNrm;
void main() {
  vec4 wp = modelMatrix * vec4(position, 1.0);
  vPos = wp.xyz;
  vNrm = normalize(mat3(modelMatrix) * normal);
  gl_Position = projectionMatrix * viewMatrix * wp;
}`;
const GLASS_FRAG = /* glsl */`
uniform samplerCube uEnv;
uniform float uR0;
uniform float uTransmit;
varying vec3 vPos;
varying vec3 vNrm;
void main() {
  vec3 V = normalize(vPos - cameraPosition);
  vec3 N = normalize(vNrm);
  if (dot(N, V) > 0.0) N = -N;
  float c = clamp(-dot(N, V), 0.0, 1.0);
  float F = uR0 + (1.0 - uR0) * pow(1.0 - c, 5.0);
  vec3 env = textureCube(uEnv, reflect(V, N)).rgb;
  gl_FragColor = vec4(env * F, (1.0 - F) * uTransmit);
}`;

function glassMaterial(envTexture) {
  return new THREE.ShaderMaterial({
    uniforms: { uEnv: { value: envTexture }, uR0: { value: 0.022 }, uTransmit: { value: 0.96 } },
    vertexShader: GLASS_VERT,
    fragmentShader: GLASS_FRAG,
    transparent: true,
    depthWrite: false,
    side: THREE.DoubleSide,
    blending: THREE.CustomBlending,
    blendEquation: THREE.AddEquation,
    blendSrc: THREE.OneFactor,
    blendDst: THREE.SrcAlphaFactor,
    blendSrcAlpha: THREE.ZeroFactor,
    blendDstAlpha: THREE.OneFactor,
  });
}

// The cube's backdrop: the Earth's light below the station's horizon, space
// above (only the reflection cube sees it).
const BACKDROP_FRAG = /* glsl */`
uniform vec3 uEarthL;
varying vec3 vDir;
void main() {
  vec3 d = normalize(vDir);
  gl_FragColor = vec4(uEarthL * (1.0 - smoothstep(-0.42, -0.30, d.z)), 1.0);
}`;

// ---------------------------------------------------------------------------
// Canvas textures for the close-out panels and the workstation's screens.

function panelTexture() {
  const W = 2048, H = 256;
  const c = document.createElement('canvas');
  c.width = W; c.height = H;
  const g = c.getContext('2d');
  g.fillStyle = '#d3d5d4';
  g.fillRect(0, 0, W, H);
  let seed = 7;
  const rnd = () => ((seed = (seed * 16807) % 2147483647) / 2147483647);
  const cols = 14;
  const cw = W / cols;
  for (let i = 0; i < cols; i++) {
    const x = i * cw;
    const split = H * (0.42 + rnd() * 0.16);
    for (const [y0, y1] of [[0, split], [split, H]]) {
      const shade = 200 + Math.floor(rnd() * 18);
      g.fillStyle = `rgb(${shade},${shade + 2},${shade + 1})`;
      g.fillRect(x + 3, y0 + 3, cw - 6, y1 - y0 - 6);
      g.fillStyle = '#7d8183';
      for (const [fx, fy] of [[x + 10, y0 + 10], [x + cw - 10, y0 + 10], [x + 10, y1 - 10], [x + cw - 10, y1 - 10]]) {
        g.beginPath(); g.arc(fx, fy, 2.6, 0, Math.PI * 2); g.fill();
      }
    }
    g.fillStyle = '#5d6163';
    g.fillRect(x, 0, 2, H);
    g.fillRect(x, split - 1, cw, 2);
    // Placards, Velcro and the odd coloured label.
    if (rnd() > 0.35) {
      const px = x + 20 + rnd() * (cw - 90), py = 20 + rnd() * (H - 70);
      g.fillStyle = '#f2f1ea'; g.fillRect(px, py, 52, 26);
      g.fillStyle = '#3a3c3e';
      for (let l = 0; l < 3; l++) g.fillRect(px + 5, py + 6 + l * 6, 30 + rnd() * 12, 2);
    }
    if (rnd() > 0.45) {
      g.fillStyle = '#56595b';
      g.fillRect(x + 14 + rnd() * (cw - 50), 30 + rnd() * (H - 70), 22, 22);
    }
    if (rnd() > 0.8) {
      g.fillStyle = rnd() > 0.5 ? '#b8473c' : '#c99a2e';
      g.fillRect(x + 16 + rnd() * (cw - 60), 18 + rnd() * (H - 40), 34, 8);
    }
  }
  const t = new THREE.CanvasTexture(c);
  t.colorSpace = THREE.SRGBColorSpace;
  t.wrapS = THREE.RepeatWrapping;
  t.anisotropy = 4;
  return t;
}

function screenTexture(kind) {
  const W = 256, H = 170;
  const c = document.createElement('canvas');
  c.width = W; c.height = H;
  const g = c.getContext('2d');
  const grad = g.createLinearGradient(0, 0, 0, H);
  if (kind === 'camera') {
    grad.addColorStop(0, '#2b3238'); grad.addColorStop(1, '#4b5157');
    g.fillStyle = grad; g.fillRect(0, 0, W, H);
    g.strokeStyle = '#9fb8a6'; g.lineWidth = 1.5;
    g.beginPath(); g.moveTo(W / 2 - 18, H / 2); g.lineTo(W / 2 + 18, H / 2); g.moveTo(W / 2, H / 2 - 18); g.lineTo(W / 2, H / 2 + 18); g.stroke();
    g.fillStyle = '#d8dde0'; g.fillRect(8, 8, 46, 7);
  } else {
    grad.addColorStop(0, '#102033'); grad.addColorStop(1, '#0b1520');
    g.fillStyle = grad; g.fillRect(0, 0, W, H);
    g.fillStyle = '#5fa3c9';
    for (let r = 0; r < 9; r++) g.fillRect(12, 14 + r * 16, 70 + ((r * 37) % 90), 5);
    g.fillStyle = '#7dd3a8'; g.fillRect(190, 20, 48, 48);
  }
  const t = new THREE.CanvasTexture(c);
  t.colorSpace = THREE.SRGBColorSpace;
  return t;
}

// ---------------------------------------------------------------------------
// The module itself.

// Draw order: every part shares the module's origin, so the renderer's own
// depth sort cannot tell near from far. The wells and the outer shell, seen
// only through the openings, draw after the faces in front of them, so the
// depth test turns most of their pixels away before they are shaded.
const BEHIND = 1;

function buildCupola() {
  const root = new THREE.Group();
  root.name = 'Cupola';
  const glass = new THREE.Group();
  glass.name = 'Cupola glass';
  const box = new THREE.Box3();

  // Colours are sRGB hex, decoded to linear albedo: black anodised frames
  // and wells 4 to 6%, grey anodised fittings 30 to 40%, the white
  // close-out panels about 65%.
  const darkFrame = interiorMaterial({ color: 0x3a3b3e, rough: 0.55, metal: 0.25 });
  const bezelMat = interiorMaterial({ color: 0x4a4b4f, rough: 0.42, metal: 0.4 });
  // The wells are a matte finish: polished, they glint white along their
  // length whenever the Sun shines down them.
  const revealMat = interiorMaterial({ color: 0x404246, rough: 0.88, metal: 0.0 });
  const silverMat = interiorMaterial({ color: 0x9a9ea2, rough: 0.42, metal: 0.6 });
  const boltMat = interiorMaterial({ color: 0x9fa3a7, rough: 0.35, metal: 0.9 });
  const greyMat = interiorMaterial({ color: 0x9da1a4, rough: 0.5, metal: 0.5 });
  const panelMat = interiorMaterial({ color: 0xffffff, rough: 0.7, metal: 0.0, map: panelTexture() });
  const ceilingMat = interiorMaterial({ color: 0xc9cbca, rough: 0.75, metal: 0.0 });
  const railMat = interiorMaterial({ color: 0x4b7fb0, rough: 0.38, metal: 0.5 });
  const trackMat = interiorMaterial({ color: 0x6a6e72, rough: 0.45, metal: 0.7 });
  const kitMat = interiorMaterial({ color: 0x3c3e42, rough: 0.6, metal: 0.2 });
  const luminaire = interiorMaterial({ color: 0xf2efe6, rough: 0.8, emissive: 0xfff1dc, emissiveGain: 0.16 });
  const add = (geo, mat, parent = root) => { const m = new THREE.Mesh(geo, mat); m.frustumCulled = false; parent.add(m); return m; };

  // The top window: the hexagonal frame plate round it, the retaining ring,
  // its reveal and the two outer panes.
  const hexCorner = HEX_APOTHEM / Math.cos(30 * DEG);
  const plate = shapeFrom(regularPolygon(6, hexCorner, 30 * DEG));
  plate.holes.push(new THREE.Path().absarc(0, 0, TOP_R, 0, Math.PI * 2, true));
  add(new THREE.ShapeGeometry(plate, 64), darkFrame);
  const ring = new THREE.RingGeometry(TOP_R - 0.002, TOP_FRAME_R, 96, 1);
  add(ring, silverMat).position.z = 0.004;
  const lip = new THREE.CylinderGeometry(TOP_FRAME_R, TOP_FRAME_R, 0.026, 96, 1, true);
  lip.rotateX(Math.PI / 2);
  add(lip, silverMat).position.z = 0.015;
  const topSkin = shapeFrom(regularPolygon(6, hexCorner, 30 * DEG));
  topSkin.holes.push(new THREE.Path().absarc(0, 0, TOP_R + TOP_FLARE, 0, Math.PI * 2, true));
  const topOuter = add(new THREE.ShapeGeometry(topSkin, 64), darkFrame);
  topOuter.position.z = -TOP_DEPTH;
  topOuter.renderOrder = BEHIND;
  const topReveal = new THREE.CylinderGeometry(TOP_R, TOP_R + TOP_FLARE, TOP_DEPTH, 96, 1, true);
  topReveal.rotateX(Math.PI / 2);
  const topWell = add(topReveal, revealMat);
  topWell.position.z = -TOP_DEPTH / 2;
  topWell.renderOrder = BEHIND;

  // The six side faces, each a trapezoid in the frame between the hexagon
  // and the ring, with its window. Face coordinates: x across, y up the
  // slope from the hexagon's edge.
  const e0 = 2 * HEX_APOTHEM * Math.tan(30 * DEG);
  const e1 = 2 * RING_APOTHEM * Math.tan(30 * DEG);
  const aperture = [[-SIDE_SHORT / 2, APERTURE_V0], [SIDE_SHORT / 2, APERTURE_V0], [SIDE_LONG / 2, APERTURE_TOP_V], [-SIDE_LONG / 2, APERTURE_TOP_V]];
  const apertureRound = filletPolygon(aperture, SIDE_CORNER, 4);
  const bezelOuter = filletPolygon(offsetConvex(aperture, 0.05), SIDE_CORNER + 0.05, 4);
  const apertureOuter = filletPolygon(offsetConvex(aperture, SIDE_FLARE), SIDE_CORNER + SIDE_FLARE, 4);
  const paneOuter = filletPolygon(offsetConvex(aperture, SIDE_FLARE * 0.93), SIDE_CORNER + SIDE_FLARE * 0.93, 4);
  const faceGeo = (() => {
    const s = shapeFrom([[-e0 / 2, 0], [e0 / 2, 0], [e1 / 2, FACET_LEN], [-e1 / 2, FACET_LEN]]);
    s.holes.push(pathFrom(apertureRound));
    return new THREE.ShapeGeometry(s);
  })();
  // The frame is solid through the window's thickness: each face has an
  // outer skin with the well's outer opening, so a sight line past a well
  // meets metal, and closeOuterShell below joins the skins up.
  const skinGeo = (() => {
    const s = shapeFrom([[-e0 / 2, 0], [e0 / 2, 0], [e1 / 2, FACET_LEN], [-e1 / 2, FACET_LEN]]);
    s.holes.push(pathFrom(apertureOuter));
    return new THREE.ShapeGeometry(s);
  })();
  const bezelGeo = (() => {
    const s = shapeFrom(bezelOuter);
    s.holes.push(pathFrom(apertureRound));
    return new THREE.ExtrudeGeometry(s, { depth: 0.022, bevelEnabled: false });
  })();
  const paneGeos = [new THREE.ShapeGeometry(shapeFrom(apertureRound)), new THREE.ShapeGeometry(shapeFrom(paneOuter))];
  const boltGeo = new THREE.CylinderGeometry(0.0042, 0.0042, 0.006, 10);
  boltGeo.rotateX(Math.PI / 2);
  const bolts = [];
  const basis = new THREE.Matrix4();
  const U = new THREE.Vector3(), V = new THREE.Vector3(), N = new THREE.Vector3(), O = new THREE.Vector3();
  SIDE_AZIMUTHS.forEach((phi, i) => {
    const out = new THREE.Vector3(Math.cos(phi), Math.sin(phi), 0);
    U.set(-Math.sin(phi), Math.cos(phi), 0);
    V.copy(out).multiplyScalar(FACET_COS).setZ(FACET_SIN);
    N.copy(out).multiplyScalar(-FACET_SIN).setZ(FACET_COS);
    O.copy(out).multiplyScalar(HEX_APOTHEM);
    const X = U.clone().negate();
    basis.makeBasis(X, V, N).setPosition(O);
    const face = add(faceGeo, darkFrame);
    face.matrixAutoUpdate = false;
    face.matrix.copy(basis);
    const skin = add(skinGeo, darkFrame);
    skin.renderOrder = BEHIND;
    skin.matrixAutoUpdate = false;
    skin.matrix.copy(basis).premultiply(new THREE.Matrix4().makeTranslation(N.x * -SIDE_DEPTH, N.y * -SIDE_DEPTH, N.z * -SIDE_DEPTH));
    const bezel = add(bezelGeo, bezelMat);
    bezel.matrixAutoUpdate = false;
    bezel.matrix.copy(basis);
    // The well through the window's thickness, and the inner and outer
    // panes.
    const place = (pts, depth) => pts.map(([x, y]) => new THREE.Vector3().copy(O).addScaledVector(X, x).addScaledVector(V, y).addScaledVector(N, -depth));
    add(loftWall(place(apertureRound, 0), place(apertureOuter, SIDE_DEPTH)), revealMat).renderOrder = BEHIND;
    for (const [k, depth] of [0.012, SIDE_DEPTH - 0.012].entries()) {
      const pane = add(paneGeos[k], null, glass);
      pane.matrixAutoUpdate = false;
      pane.matrix.copy(basis).premultiply(new THREE.Matrix4().makeTranslation(N.x * -depth, N.y * -depth, N.z * -depth));
    }
    // Bolts round the frame.
    const ringPts = filletPolygon(offsetConvex(aperture, 0.03), SIDE_CORNER + 0.03, 2);
    let perimeter = 0;
    for (let k = 0; k < ringPts.length; k++) {
      const a = ringPts[k], b = ringPts[(k + 1) % ringPts.length];
      perimeter += Math.hypot(b[0] - a[0], b[1] - a[1]);
    }
    const count = Math.round(perimeter / 0.075);
    for (let k = 0; k < count; k++) {
      let target = (k / count) * perimeter;
      for (let j = 0; j < ringPts.length; j++) {
        const a = ringPts[j], b = ringPts[(j + 1) % ringPts.length];
        const len = Math.hypot(b[0] - a[0], b[1] - a[1]);
        if (target <= len) {
          const f = target / len;
          bolts.push(new THREE.Vector3().copy(O).addScaledVector(X, a[0] + (b[0] - a[0]) * f).addScaledVector(V, a[1] + (b[1] - a[1]) * f).addScaledVector(N, 0.023).toArray().concat(N.toArray()));
          break;
        }
        target -= len;
      }
    }
    // The shutter's hand crank, on the frame above the window.
    const crankBase = new THREE.Vector3().copy(O).addScaledVector(X, 0.29).addScaledVector(V, APERTURE_TOP_V + 0.035);
    const housing = new THREE.CylinderGeometry(0.026, 0.03, 0.05, 16);
    housing.rotateX(Math.PI / 2);
    const h = add(housing, greyMat);
    h.matrixAutoUpdate = false;
    h.matrix.copy(basis).setPosition(crankBase.clone().addScaledVector(N, 0.025));
    const handle = new THREE.BoxGeometry(0.075, 0.014, 0.014);
    const hd = add(handle, kitMat);
    hd.matrixAutoUpdate = false;
    hd.matrix.copy(basis).setPosition(crankBase.clone().addScaledVector(N, 0.056));
    // The mullion on this face's left edge (toward the next window round)
    // carries two seat tracks, one on each face beside the corner.
    const cornerDir = new THREE.Vector3(Math.cos(phi + 30 * DEG), Math.sin(phi + 30 * DEG), 0);
    const cornerA = cornerDir.clone().multiplyScalar(hexCorner);
    const cornerB = cornerDir.clone().multiplyScalar(RING_APOTHEM / Math.cos(30 * DEG)).setZ(RING_Z);
    const along = cornerB.clone().sub(cornerA);
    const length = along.length();
    along.normalize();
    const mid = cornerA.clone().add(cornerB).multiplyScalar(0.5);
    for (const facePhi of [phi, phi + 60 * DEG]) {
      const faceOut = new THREE.Vector3(Math.cos(facePhi), Math.sin(facePhi), 0);
      const faceN = faceOut.clone().multiplyScalar(-FACET_SIN).setZ(FACET_COS);
      const centre = faceOut.clone().multiplyScalar(HEX_APOTHEM + 0.5 * FACET_LEN * FACET_COS).setZ(0.5 * FACET_LEN * FACET_SIN);
      const inward = centre.sub(mid);
      inward.addScaledVector(along, -inward.dot(along)).addScaledVector(faceN, -inward.dot(faceN)).normalize();
      const m = add(new THREE.BoxGeometry(0.022, length * 0.72, 0.009), trackMat);
      m.matrixAutoUpdate = false;
      const at = cornerA.clone().addScaledVector(along, length * 0.52).addScaledVector(inward, 0.07).addScaledVector(faceN, 0.0045);
      m.matrix.makeBasis(new THREE.Vector3().crossVectors(along, faceN).normalize(), along, faceN).setPosition(at);
    }
  });
  closeOuterShell(root, darkFrame, hexCorner, e0, e1);
  // Bolts round the top window's ring.
  for (let k = 0; k < 32; k++) {
    const a = (k / 32) * Math.PI * 2;
    bolts.push([Math.cos(a) * (TOP_FRAME_R - 0.018), Math.sin(a) * (TOP_FRAME_R - 0.018), 0.007, 0, 0, 1]);
  }
  const boltMesh = new THREE.InstancedMesh(boltGeo, boltMat, bolts.length);
  const q = new THREE.Quaternion();
  const zAxis = new THREE.Vector3(0, 0, 1);
  bolts.forEach((b, k) => {
    q.setFromUnitVectors(zAxis, new THREE.Vector3(b[3], b[4], b[5]));
    boltMesh.setMatrixAt(k, new THREE.Matrix4().compose(new THREE.Vector3(b[0], b[1], b[2]), q, new THREE.Vector3(1, 1, 1)));
  });
  boltMesh.frustumCulled = false;
  root.add(boltMesh);

  // Top window panes.
  for (const depth of [0.012, TOP_DEPTH - 0.012]) {
    add(new THREE.CircleGeometry(TOP_R + TOP_FLARE * (depth / TOP_DEPTH), 96), null, glass).position.z = -depth;
  }

  // The step between the dome and the cylindrical section, with the upper
  // handrail running round above it, then the close-out panels, the lower
  // handrail, the ceiling and the hatch up into Node 3.
  const step = shapeFrom(regularPolygon(96, WALL_R + 0.01, 0));
  step.holes.push(pathFrom(regularPolygon(6, RING_APOTHEM / Math.cos(30 * DEG), 30 * DEG)));
  add(new THREE.ShapeGeometry(step), greyMat).position.z = RING_Z;
  const wall = new THREE.CylinderGeometry(WALL_R, WALL_R, CEILING_Z - RING_Z + 0.02, 96, 1, true);
  wall.rotateX(Math.PI / 2);
  add(wall, panelMat).position.z = (RING_Z + CEILING_Z) / 2;
  const railGeo = new THREE.TorusGeometry(WALL_R - 0.11, 0.0135, 10, 160);
  add(railGeo, railMat).position.z = RING_Z + 0.075;
  const lowRail = new THREE.TorusGeometry(WALL_R - 0.06, 0.0135, 10, 160);
  add(lowRail, railMat).position.z = CEILING_Z - 0.2;
  const standoff = new THREE.CylinderGeometry(0.008, 0.008, 0.075, 8);
  for (let k = 0; k < 12; k++) {
    const a = (k + 0.5) * 30 * DEG;
    const s1 = add(standoff, greyMat);
    s1.position.set(Math.cos(a) * (WALL_R - 0.11), Math.sin(a) * (WALL_R - 0.11), RING_Z + 0.037);
    s1.rotation.x = Math.PI / 2;
    const s2 = add(standoff, greyMat);
    s2.position.set(Math.cos(a) * (WALL_R - 0.03), Math.sin(a) * (WALL_R - 0.03), CEILING_Z - 0.2);
    s2.rotation.z = a + Math.PI / 2;
  }
  const hatch = filletPolygon([[-HATCH_HALF, -HATCH_HALF], [HATCH_HALF, -HATCH_HALF], [HATCH_HALF, HATCH_HALF], [-HATCH_HALF, HATCH_HALF]], 0.16, 6);
  const ceiling = shapeFrom(regularPolygon(96, WALL_R + 0.01, 0));
  ceiling.holes.push(pathFrom(hatch));
  const ceilingGeo = new THREE.ShapeGeometry(ceiling);
  ceilingGeo.rotateX(Math.PI);
  add(ceilingGeo, ceilingMat).position.z = CEILING_Z;
  add(loftWall(hatch.map(([x, y]) => new THREE.Vector3(x, y, CEILING_Z)), hatch.map(([x, y]) => new THREE.Vector3(x, y, HATCH_TOP))), ceilingMat);
  const capGeo = new THREE.ShapeGeometry(shapeFrom(hatch));
  capGeo.rotateX(Math.PI);
  add(capGeo, kitMat).position.z = HATCH_TOP;
  const lamp = new THREE.PlaneGeometry(0.9, 0.12);
  lamp.rotateX(Math.PI);
  add(lamp, luminaire).position.set(0, 0.3, HATCH_TOP - 0.01);

  // The robotic workstation on the forward side: three displays, the
  // control panel under them and the two hand controllers.
  const rws = new THREE.Group();
  rws.name = 'Robotic workstation';
  const displayGeo = new THREE.BoxGeometry(0.31, 0.21, 0.022);
  const screenGeo = new THREE.PlaneGeometry(0.28, 0.18);
  const screens = [screenTexture('camera'), screenTexture('camera'), screenTexture('ui')];
  [-1, 0, 1].forEach((k, i) => {
    // Against the wall between the handrails, tilted down into the dome.
    const a = k * 17 * DEG;
    const r = WALL_R - 0.08;
    const g = new THREE.Group();
    g.position.set(Math.cos(a) * r, Math.sin(a) * r, CEILING_Z - 0.39);
    g.lookAt(0, 0, 0.45);
    add(displayGeo, kitMat, g);
    const screen = add(screenGeo, interiorMaterial({ color: 0x050607, rough: 0.25, metal: 0.0, emissive: 0xffffff, emissiveGain: 0.06, emissiveMap: screens[i] }), g);
    screen.position.z = 0.0115;
    rws.add(g);
  });
  // The control panel, flush on the wall under the displays.
  const panelBox = new THREE.Group();
  panelBox.position.set(WALL_R - 0.025, 0, RING_Z + 0.2);
  panelBox.lookAt(0, 0, RING_Z + 0.2);
  add(new THREE.BoxGeometry(0.36, 0.13, 0.03), greyMat, panelBox);
  rws.add(panelBox);
  for (const k of [-1, 1]) {
    const a = k * 36 * DEG;
    const hc = new THREE.Group();
    hc.position.set(Math.cos(a) * (WALL_R - 0.2), Math.sin(a) * (WALL_R - 0.2), RING_Z + 0.04);
    add(new THREE.BoxGeometry(0.1, 0.09, 0.07), kitMat, hc).position.z = 0.035;
    const stick = add(new THREE.CylinderGeometry(0.011, 0.014, 0.085, 10), greyMat, hc);
    stick.rotation.x = Math.PI / 2;
    stick.position.z = 0.11;
    rws.add(hc);
  }
  root.add(rws);

  root.updateMatrixWorld(true);
  glass.updateMatrixWorld(true);
  box.setFromObject(root);
  return { root, glass, bounds: box };
}

// The outer shell between the skins: the mullion corners between
// neighbouring faces, the strip from each face down to the top window's
// skin, and the triangles at the hexagon's corners. Every piece lies inside
// the outer planes of the windows next to it, so nothing seen through a
// window is covered.
function closeOuterShell(root, material, hexCorner, e0, e1) {
  const facetPoint = (j, x, y, depth) => {
    const phi = SIDE_AZIMUTHS[(j + 6) % 6];
    const out = new THREE.Vector3(Math.cos(phi), Math.sin(phi), 0);
    const X = new THREE.Vector3(Math.sin(phi), -Math.cos(phi), 0);
    const V = out.clone().multiplyScalar(FACET_COS).setZ(FACET_SIN);
    const N = out.clone().multiplyScalar(-FACET_SIN).setZ(FACET_COS);
    return out.multiplyScalar(HEX_APOTHEM).addScaledVector(X, x).addScaledVector(V, y).addScaledVector(N, -depth);
  };
  const topCorner = (azimuth) => new THREE.Vector3(Math.cos(azimuth) * hexCorner, Math.sin(azimuth) * hexCorner, -TOP_DEPTH);
  const pos = [];
  const tri = (a, b, c) => pos.push(a.x, a.y, a.z, b.x, b.y, b.z, c.x, c.y, c.z);
  const quad = (a, b, c, d) => { tri(a, b, c); tri(a, c, d); };
  for (let j = 0; j < 6; j++) {
    const phi = SIDE_AZIMUTHS[j];
    // Face j's x = +e/2 edge is toward face j - 1, x = -e/2 toward j + 1.
    const innerRight = facetPoint(j, e0 / 2, 0, SIDE_DEPTH);
    const innerLeft = facetPoint(j, -e0 / 2, 0, SIDE_DEPTH);
    quad(topCorner(phi - 30 * DEG), topCorner(phi + 30 * DEG), innerLeft, innerRight);
    const nextInner = facetPoint(j + 1, e0 / 2, 0, SIDE_DEPTH);
    tri(topCorner(phi + 30 * DEG), innerLeft, nextInner);
    quad(innerLeft, facetPoint(j, -e1 / 2, FACET_LEN, SIDE_DEPTH), facetPoint(j + 1, e1 / 2, FACET_LEN, SIDE_DEPTH), nextInner);
  }
  const g = new THREE.BufferGeometry();
  g.setAttribute('position', new THREE.Float32BufferAttribute(pos, 3));
  g.setAttribute('uv', new THREE.Float32BufferAttribute(new Array((pos.length / 3) * 2).fill(0), 2));
  g.computeVertexNormals();
  const m = new THREE.Mesh(g, material);
  m.renderOrder = BEHIND;
  m.frustumCulled = false;
  root.add(m);
}

// The view, as pure functions of the look (yaw and pitch in radians, yaw 0
// toward the velocity, pitch -90 degrees straight down) and the head's
// height above the top window (metres).

// The eye: ahead of a neck pivot, leaning down and toward the window looked
// out of as the gaze comes up toward the horizon.
export function cupolaEye(out, yaw, pitch, head) {
  const lean = smooth(pitch, -80 * DEG, -22 * DEG);
  const h = Math.max(EYE_MIN, head - LEAN_DOWN * lean);
  const r = LEAN_OUT * lean;
  const cp = Math.cos(pitch);
  return out.set((r + NECK * cp) * Math.cos(yaw), (r + NECK * cp) * Math.sin(yaw), h + NECK + NECK * Math.sin(pitch));
}

// The highest the gaze may come up: a little under the far edge of a side
// window's opening (the inner glass or the outer end of the well, whichever
// cuts lower) seen from the head at its fullest lean.
export function cupolaPitchLimit(head) {
  const pitch = -20 * DEG;
  const z = Math.max(EYE_MIN, head - LEAN_DOWN) + NECK + NECK * Math.sin(pitch);
  const r = LEAN_OUT + NECK * Math.cos(pitch);
  const inner = Math.atan2(APERTURE_TOP_Z - z, APERTURE_TOP_R - r);
  const outerR = APERTURE_TOP_R + SIDE_DEPTH * FACET_SIN + SIDE_FLARE * FACET_COS;
  const outerZ = APERTURE_TOP_Z - SIDE_DEPTH * FACET_COS + SIDE_FLARE * FACET_SIN;
  const outer = Math.atan2(outerZ - z, outerR - r);
  return Math.min(-6 * DEG, Math.max(-60 * DEG, Math.min(inner, outer) - 3 * DEG));
}

// The lens, vertical degrees: about 72 on the screen's shorter side, as a
// wide lens in the dome sees it, at most 110 across a very wide screen and
// 90 tall on a phone held upright.
export function cupolaFov(aspect) {
  const a = Math.max(aspect, 0.1);
  if (a >= 1) return Math.min(72, 2 * Math.atan(Math.tan(55 * DEG) / a) / DEG);
  return Math.min(90, 2 * Math.atan(Math.tan(36 * DEG) / a) / DEG);
}

// JS twin of the shader's window test, for the glare: whether a ray from p
// (module metres) along L leaves through a window's glass.
export function sunThroughWindow(p, L) {
  const soft = (d) => Math.min(1, Math.max(0, d / 0.012 + 0.5));
  const top = (z, r) => {
    if (p.z <= z) return 1;
    if (L.z >= -1e-4) return 0;
    const t = (z - p.z) / L.z;
    return soft(r - Math.hypot(p.x + L.x * t, p.y + L.y * t));
  };
  let s = top(-0.004, TOP_R) * top(-TOP_DEPTH, TOP_R + TOP_FLARE);
  const u = SHARED_UNIFORMS;
  const h = new THREE.Vector3();
  const o = new THREE.Vector3();
  for (let i = 0; i < 6; i++) {
    const n = u.uWinN.value[i];
    let both = 1;
    for (const [off, grow] of [[0.004, 0], [SIDE_DEPTH, SIDE_FLARE]]) {
      o.copy(u.uWinO.value[i]).addScaledVector(n, off);
      const ahead = h.copy(o).sub(p).dot(n);
      if (ahead <= 0) continue;
      const dn = L.dot(n);
      if (dn <= 1e-4) { both = 0; break; }
      h.copy(p).addScaledVector(L, ahead / dn).sub(o);
      const uu = h.dot(u.uWinU.value[i]);
      const vv = h.dot(u.uWinV.value[i]);
      const hw = THREE.MathUtils.lerp(SIDE_SHORT / 2, SIDE_LONG / 2, Math.min(1, Math.max(0, vv / SIDE_H))) + grow;
      both *= soft(vv + grow) * soft(SIDE_H + grow - vv) * soft(hw - Math.abs(uu));
    }
    s = Math.max(s, both);
  }
  return s;
}

const STYLE = `
#cupola-ui, .iss-live-cupola {
  --rk-ink: #ece7da; --rk-ink-bright: #ece7da; --rk-ink-dim: #a8a396;
  --rk-ink-mute: #a8a396; --rk-fill: #141922; --rk-fill-hover: #1b212c;
  --rk-hud: #0a0d13; --rk-green: #7dd3a8;
}
.iss-live-actions .iss-live-cupola {
  margin-left: auto; display: inline-flex; align-items: center; gap: 8px;
  background: var(--rk-fill); border: none; color: var(--rk-ink-bright);
  font: 500 0.78rem/1 'Inter', sans-serif;
  min-height: 36px; padding: 0 12px 0 10px; border-radius: 8px; cursor: pointer; white-space: nowrap;
  transition: background-color 0.15s ease;
}
.iss-live-actions .iss-live-cupola:hover { background: var(--rk-fill-hover); }
.iss-live-actions .iss-live-cupola:focus-visible { outline: 2px solid #7dd3a8; outline-offset: 2px; }
.iss-live-actions .iss-live-cupola:disabled { opacity: 0.45; cursor: default; background: var(--rk-fill); }
.iss-live-actions .iss-live-cupola svg { width: 14px; height: 14px; flex: none; color: var(--rk-green); }
.iss-live-actions .iss-live-cupola kbd {
  font: 500 0.7rem/1 'Inter', sans-serif; color: var(--rk-ink-mute); background: #0a0d13;
  border: none; border-radius: 4px; padding: 3px 5px;
}
.iss-live-actions .iss-live-cupola + .iss-live-stop { margin-left: 0; }
@media (max-width: 820px), (hover: none) { .iss-live-actions .iss-live-cupola kbd { display: none; } }
@media (max-width: 480px) {
  .iss-live-actions:has(.iss-live-cupola) { gap: 8px; }
  .iss-live-actions .iss-live-cupola .cu-word { display: none; }
  .iss-live-actions:has(.iss-live-cupola) .iss-live-link, .iss-live-actions:has(.iss-live-cupola) .iss-live-stop { white-space: nowrap; }
}
@media (max-width: 380px) { .iss-live-actions:has(.iss-live-cupola) .iss-live-link { font-size: 0.72rem; } }

#cupola-fade { position: fixed; inset: 0; z-index: 54; background: #000; opacity: 0; pointer-events: none; }
#cupola-ui { position: fixed; inset: 0; z-index: 60; pointer-events: none; font-family: 'Inter', sans-serif; color: var(--rk-ink); }
#cupola-ui[hidden] { display: none; }
#cupola-ui .cu-bar {
  position: absolute; top: max(16px, env(safe-area-inset-top, 0px)); left: 50%; transform: translateX(-50%);
  display: flex; align-items: center; gap: 12px; max-width: calc(100vw - 24px); box-sizing: border-box;
  padding: 6px 6px 6px 14px; background: var(--rk-hud); border: none; border-radius: 12px;
  pointer-events: auto; transition: opacity 0.6s ease;
}
#cupola-ui.is-idle .cu-bar { opacity: 0.38; }
#cupola-ui.is-idle .cu-bar:hover { opacity: 1; }
#cupola-ui .cu-label { display: flex; align-items: center; gap: 8px; font-size: 0.82rem; font-weight: 500; color: var(--rk-ink); flex: none; }
#cupola-ui .cu-caption { font-size: 0.82rem; color: var(--rk-ink-dim); font-variant-numeric: tabular-nums; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; min-width: 0; }
#cupola-ui .cu-leave {
  flex: none; display: inline-flex; align-items: center; gap: 8px; background: var(--rk-fill);
  border: none; color: var(--rk-ink); font: 500 0.8rem/1 'Inter', sans-serif;
  min-height: 36px; padding: 0 8px 0 12px; border-radius: 8px; cursor: pointer; transition: background-color 0.15s ease;
}
#cupola-ui .cu-leave:hover { background: var(--rk-fill-hover); }
#cupola-ui .cu-leave:focus-visible { outline: 2px solid #7dd3a8; outline-offset: 2px; }
#cupola-ui .cu-leave kbd { font: 500 0.7rem/1 'Inter', sans-serif; color: var(--rk-ink-mute); background: #0a0d13; border-radius: 4px; padding: 3px 5px; }
#cupola-ui .cu-hint {
  position: absolute; left: 50%; bottom: max(26px, calc(env(safe-area-inset-bottom, 0px) + 18px)); transform: translateX(-50%);
  font-size: 0.8rem; color: var(--rk-ink); white-space: nowrap;
  padding: 8px 14px; background: var(--rk-hud); border-radius: 12px; transition: opacity 0.8s ease; opacity: 0;
}
#cupola-ui .cu-hint.on { opacity: 1; }
@media (max-width: 560px) {
  #cupola-ui .cu-bar { gap: 9px; padding-left: 12px; }
  #cupola-ui .cu-label span.cu-name { display: none; }
  #cupola-ui .cu-leave kbd { display: none; }
  #cupola-ui .cu-leave { padding-right: 12px; }
  #cupola-ui .cu-hint { white-space: normal; text-align: center; width: max-content; max-width: calc(100vw - 40px); }
}
body.cupola-mode #controls-panel, body.cupola-mode #planet-nav, body.cupola-mode #distance-indicator,
body.cupola-mode #controls-hint, body.cupola-mode #matryoshka, body.cupola-mode #title,
body.cupola-mode #science-panel, body.cupola-mode #object-browser,
body.cupola-mode #space-events, body.cupola-mode #event-caption, body.cupola-mode .mobile-back,
body.cupola-mode #mobile-menu-fab, body.cupola-mode #mobile-panel-backdrop, body.cupola-mode #tuner-dock,
body.cupola-mode #back-button, body.cupola-mode #sim-date-display, body.cupola-mode #quality-picker,
body.cupola-mode #share-toggle, body.cupola-mode #object-browser-toggle, body.cupola-mode #space-events-toggle,
body.cupola-mode .iss-live-card, body.cupola-mode .iss-track-fab {
  opacity: 0 !important; pointer-events: none !important; transition: opacity 0.5s ease;
}
body.cupola-mode #planet-hud-container, body.cupola-mode #hover-label, body.cupola-mode .vt-tip { display: none !important; }
`;

const CUPOLA_ICON = '<svg viewBox="0 0 16 16" fill="none" stroke="currentColor" stroke-width="1.3" aria-hidden="true"><path d="M8 1.6 13.6 4.8v6.4L8 14.4 2.4 11.2V4.8Z"/><circle cx="8" cy="8" r="2.3"/><path d="M8 1.6v4.1M13.6 4.8 10 6.9M13.6 11.2 10 9.1M8 14.4v-4.1M2.4 11.2 6 9.1M2.4 4.8 6 6.9"/></svg>';

// api: { getCamera, getControls, getRenderer, getComposer, getComposerRenderPass,
//        getCinematicPass, getIss, getEarth, getEarthRadius, getSunPosition,
//        getSatrec, getSimDate, isIssFocused, isRealistic, setRealistic, blockedBy }
export function createCupolaView(api) {
  let phase = 'off'; // off, entering, inside, leaving-out, leaving-in
  let phaseTime = 0;
  let built = null;
  let scene = null, cam = null, pass = null, attachedComposer = null;
  let cubeTarget = null, cubeCamera = null, backdrop = null, cubeAge = Infinity;
  let saved = null;
  let sunWindowVis = 1;
  let caption = '';
  let lastInput = 0;
  let hintTimer = 0;
  let pendingEnter = 0;

  const look = { yaw: 0, pitch: -90 * DEG, yawT: 0, pitchT: -90 * DEG, head: HEAD_DEFAULT, headT: HEAD_DEFAULT };
  const frame = { origin: new THREE.Vector3(), x: new THREE.Vector3(), y: new THREE.Vector3(), z: new THREE.Vector3(), q: new THREE.Quaternion(), metre: 0 };
  const tmp = new THREE.Vector3(), tmp2 = new THREE.Vector3(), earthPos = new THREE.Vector3(), sunPos = new THREE.Vector3();
  const qTmp = new THREE.Quaternion(), qLook = new THREE.Quaternion(), qInv = new THREE.Quaternion();
  const mTmp = new THREE.Matrix4();
  const eye = new THREE.Vector3();
  const sunLocal = new THREE.Vector3();
  const qBase = new THREE.Quaternion().setFromRotationMatrix(new THREE.Matrix4().makeBasis(
    new THREE.Vector3(0, -1, 0), new THREE.Vector3(0, 0, 1), new THREE.Vector3(-1, 0, 0)));
  const X_AXIS = new THREE.Vector3(1, 0, 0), Z_AXIS = new THREE.Vector3(0, 0, 1);

  // ------------------------------------------------------------------ DOM
  let styleEl = null, ui = null, fadeEl = null, cardButton = null, captionEl = null, hintEl = null;
  function ensureStyles() {
    if (styleEl) return;
    styleEl = document.createElement('style');
    styleEl.id = 'cupola-style';
    styleEl.textContent = STYLE;
    document.head.appendChild(styleEl);
  }
  function ensureUi() {
    ensureStyles();
    if (ui) return;
    fadeEl = document.createElement('div');
    fadeEl.id = 'cupola-fade';
    document.body.appendChild(fadeEl);
    ui = document.createElement('div');
    ui.id = 'cupola-ui';
    ui.hidden = true;
    ui.innerHTML = `<div class="cu-bar" role="group" aria-label="Cupola view">
        <span class="cu-label"><span class="iss-track-dot" aria-hidden="true"></span><span class="cu-name">Cupola</span></span>
        <span class="cu-caption" aria-live="polite"></span>
        <button type="button" class="cu-leave" title="Back to the station from outside (Esc)">Leave <kbd>Esc</kbd></button>
      </div>
      <div class="cu-hint"></div>`;
    document.body.appendChild(ui);
    captionEl = ui.querySelector('.cu-caption');
    hintEl = ui.querySelector('.cu-hint');
    ui.querySelector('.cu-leave').addEventListener('click', () => leave());
  }
  function mountCardButton() {
    if (cardButton) return;
    const actions = document.querySelector('#iss-live-card .iss-live-actions');
    if (!actions) return;
    ensureStyles();
    cardButton = document.createElement('button');
    cardButton.type = 'button';
    cardButton.className = 'iss-live-cupola';
    cardButton.id = 'iss-live-cupola';
    cardButton.title = 'Look down at the Earth from inside the station’s observation module (C)';
    cardButton.innerHTML = `${CUPOLA_ICON}<span>Cupola<span class="cu-word"> view</span></span><kbd>C</kbd>`;
    cardButton.addEventListener('click', () => enter());
    const stop = actions.querySelector('.iss-live-stop');
    actions.insertBefore(cardButton, stop || null);
  }

  // ------------------------------------------------------------ the scene
  function ensureScene() {
    if (built) return;
    built = buildCupola();
    scene = new THREE.Scene();
    scene.name = 'Cupola interior';
    scene.add(built.root);
    cam = new THREE.PerspectiveCamera(60, 1, 0.02, 30);
    cam.layers.enable(1);
    cubeTarget = new THREE.WebGLCubeRenderTarget(128, { type: THREE.HalfFloatType, generateMipmaps: true, minFilter: THREE.LinearMipmapLinearFilter });
    cubeCamera = new THREE.CubeCamera(0.02, 30, cubeTarget);
    for (const c of cubeCamera.children) { c.layers.set(0); c.layers.enable(2); }
    const glassMat = glassMaterial(cubeTarget.texture);
    built.glass.traverse((o) => { if (o.isMesh) { o.material = glassMat; o.layers.set(1); o.frustumCulled = false; o.renderOrder = 2; } });
    scene.add(built.glass);
    backdrop = new THREE.Mesh(new THREE.SphereGeometry(8, 32, 16), new THREE.ShaderMaterial({
      uniforms: { uEarthL: { value: new THREE.Vector3() } },
      vertexShader: 'varying vec3 vDir; void main() { vDir = position; gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0); }',
      fragmentShader: BACKDROP_FRAG,
      side: THREE.BackSide,
      depthWrite: false,
    }));
    backdrop.layers.set(2);
    backdrop.frustumCulled = false;
    scene.add(backdrop);
    pass = new RenderPass(scene, cam);
    pass.clear = false;
    pass.clearDepth = true;
    pass.enabled = false;
    loadModel();
  }

  // NASA's model of the inside, in place of the drawn one once it has
  // loaded (the drawn glass stays: the model's is three copies of one
  // pane). The way in waits in black for it; if it cannot load, the drawn
  // module stays.
  let model = 'none'; // none, loading, ready, failed
  function loadModel() {
    if (model !== 'none' || !api.modelUrl) return;
    model = 'loading';
    Promise.all([import('three/addons/loaders/GLTFLoader.js'), import('three/addons/loaders/DRACOLoader.js')])
      .then(([{ GLTFLoader }, { DRACOLoader }]) => {
        const draco = new DRACOLoader();
        draco.setDecoderPath(api.dracoUrl);
        const loader = new GLTFLoader();
        loader.setDRACOLoader(draco);
        return loader.loadAsync(api.modelUrl).finally(() => draco.dispose());
      })
      .then((gltf) => {
        const inside = modelInside(gltf.scene);
        for (const c of [...built.root.children]) built.root.remove(c);
        built.root.add(inside);
        built.root.updateMatrixWorld(true);
        built.bounds.setFromObject(built.root);
        clearance = headClearance(inside);
        // The lamp: Tranquility's lights, on its walls round the hatch.
        SHARED_UNIFORMS.uLampPos.value.set(0, -0.8, 2.6);
        model = 'ready';
      })
      .catch((err) => {
        console.warn('[cupola] NASA model not loaded, the drawn module stays:', err);
        model = 'failed';
      });
  }

  // How far out the eye may lean, by azimuth (5 degree steps) and height
  // (5 cm steps up to 1.4 m): the nearest of the model's vertices there,
  // so leaning toward the workstation's laptops or a mullion stops short
  // of them.
  const CLEAR_AZ = 72, CLEAR_Z = 28, CLEAR_GAP = 0.2;
  let clearance = null;
  function headClearance(group) {
    const c = new Float32Array(CLEAR_AZ * CLEAR_Z).fill(WALL_R);
    const v = new THREE.Vector3();
    group.traverse((m) => {
      if (!m.isMesh) return;
      const pos = m.geometry.attributes.position;
      for (let i = 0; i < pos.count; i++) {
        v.fromBufferAttribute(pos, i).applyMatrix4(m.matrix);
        if (v.z < 0 || v.z >= CLEAR_Z * 0.05) continue;
        const a = Math.floor((Math.atan2(v.y, v.x) / (2 * Math.PI) + 1) % 1 * CLEAR_AZ) % CLEAR_AZ;
        const k = a * CLEAR_Z + Math.floor(v.z / 0.05);
        c[k] = Math.min(c[k], Math.hypot(v.x, v.y));
      }
    });
    return c;
  }
  function keepClear(p) {
    const r = Math.hypot(p.x, p.y);
    if (!clearance || r < 1e-6) return;
    const a0 = Math.floor((Math.atan2(p.y, p.x) / (2 * Math.PI) + 1) % 1 * CLEAR_AZ);
    const z0 = Math.floor(p.z / 0.05);
    let room = WALL_R;
    for (let da = -2; da <= 2; da++) {
      for (let dz = -2; dz <= 2; dz++) {
        const z = z0 + dz;
        if (z < 0 || z >= CLEAR_Z) continue;
        room = Math.min(room, clearance[((a0 + da + CLEAR_AZ) % CLEAR_AZ) * CLEAR_Z + z]);
      }
    }
    const max = Math.max(0, room - CLEAR_GAP);
    if (r > max) { p.x *= max / r; p.y *= max / r; }
  }

  function modelInside(root) {
    const group = new THREE.Group();
    group.name = 'Cupola (NASA)';
    const materials = new Map();
    root.updateMatrixWorld(true);
    root.traverse((o) => {
      if (!o.isMesh || o.material.name === 'Cupola_Glass') return;
      let mat = materials.get(o.material);
      if (!mat) {
        const m = o.material;
        mat = m.name === 'Generic_Lights'
          ? interiorMaterial({ color: 0xf2efe6, rough: 0.8, emissive: 0xfff1dc, emissiveGain: 0.5 })
          : interiorMaterial({ color: 0xffffff, rough: 1, metal: 1, map: m.map, mrMap: m.metalnessMap || m.roughnessMap, normalMap: m.normalMap });
        materials.set(o.material, mat);
      }
      const mesh = new THREE.Mesh(o.geometry, mat);
      mesh.matrixAutoUpdate = false;
      mesh.matrix.copy(o.matrixWorld);
      mesh.frustumCulled = false;
      group.add(mesh);
    });
    return group;
  }

  // After the app's scene pass and the close Earth's sky and cloud pass
  // (earthclose.js inserts its own right after the scene pass, so a later
  // insert of it still lands before this one).
  function attachPass() {
    const composer = api.getComposer();
    if (!composer || !pass) return;
    if (attachedComposer === composer && composer.passes.includes(pass)) return;
    if (attachedComposer && attachedComposer !== composer) attachedComposer.removePass(pass);
    attachedComposer = composer;
    if (composer.passes.includes(pass)) return;
    let i = composer.passes.indexOf(api.getComposerRenderPass());
    if (i < 0) return;
    while (i + 1 < composer.passes.length && composer.passes[i + 1] instanceof RenderPass) i++;
    composer.insertPass(pass, i + 1);
  }

  // At true scale the glare follows the Sun's flux; inside it only shows
  // while the Sun is behind glass. Wrapped once per pass object; nested
  // wraps (the scene target wraps passes too) pass through.
  const wrapped = new WeakSet();
  let glareDepth = 0;
  function wrapGlare() {
    const p = api.getCinematicPass();
    if (!p || !p.uniforms || !p.uniforms.lensFlareSpace) return;
    if (wrapped.has(p) && p.render.__cupola) return;
    const prev = p.render;
    const fn = function (...args) {
      const u = this.uniforms.lensFlareSpace;
      if (glareDepth > 0 || phase === 'off' || phase === 'leaving-in' || sunWindowVis >= 1) return prev.apply(this, args);
      glareDepth++;
      const was = u.value;
      u.value = was * sunWindowVis;
      try { return prev.apply(this, args); } finally { u.value = was; glareDepth--; }
    };
    fn.__cupola = true;
    p.render = fn;
    wrapped.add(p);
  }

  // ------------------------------------------------------ the station frame
  // Local vertical, local horizontal: up from the Earth's centre through the
  // station, the orbit normal from the model (its +Y, which main.js keeps on
  // the orbit normal for the SGP4 state and the authored orbit alike), and
  // the velocity's horizontal part across them.
  function computeFrame() {
    const iss = api.getIss();
    const earth = api.getEarth();
    if (!iss || !earth) return false;
    iss.updateWorldMatrix(true, false);
    frame.origin.setFromMatrixPosition(iss.matrixWorld);
    earth.getWorldPosition(earthPos);
    frame.z.copy(frame.origin).sub(earthPos);
    if (frame.z.lengthSq() === 0) return false;
    frame.z.normalize();
    iss.getWorldQuaternion(qTmp);
    frame.y.set(0, 1, 0).applyQuaternion(qTmp);
    frame.y.addScaledVector(frame.z, -frame.y.dot(frame.z)).normalize();
    frame.x.crossVectors(frame.y, frame.z).normalize();
    frame.q.setFromRotationMatrix(mTmp.makeBasis(frame.x, frame.y, frame.z));
    frame.metre = api.getEarthRadius() / EARTH_METRES;
    return frame.metre > 0;
  }
  const toWorld = (local, out) => out.copy(local).applyQuaternion(frame.q).multiplyScalar(frame.metre).add(frame.origin);
  const toLocalDir = (world, out) => out.copy(world).applyQuaternion(qInv.copy(frame.q).invert());

  // ------------------------------------------------------------- the view
  function gazeLocal(out, yaw = look.yaw, pitch = look.pitch) {
    const cp = Math.cos(pitch);
    return out.set(cp * Math.cos(yaw), cp * Math.sin(yaw), Math.sin(pitch));
  }

  function lookQuaternion(out, yaw = look.yaw, pitch = look.pitch) {
    out.copy(qBase);
    out.premultiply(qTmp.setFromAxisAngle(Z_AXIS, yaw));
    return out.multiply(qTmp.setFromAxisAngle(X_AXIS, pitch));
  }


  // ------------------------------------------------------------ the light
  const SUN_WHITE = new THREE.Color(1.0, 0.97, 0.92);
  const SUN_LOW = new THREE.Color(1.0, 0.5, 0.2);
  const EARTH_DAY = new THREE.Color(0.7, 0.8, 1.0);
  const CABIN = new THREE.Color(1.0, 0.88, 0.74);
  const colTmp = new THREE.Color();
  function updateLight() {
    const sun = api.getSunPosition(sunPos);
    const u = SHARED_UNIFORMS;
    if (!sun) return;
    tmp.copy(sunPos).sub(frame.origin).normalize();
    toLocalDir(tmp, sunLocal);
    u.uSunDir.value.copy(sunLocal);
    // The Sun from the station: hidden by the Earth (with a little air) and
    // reddened through the limb as it sets or rises.
    const R = api.getEarthRadius();
    const dist = tmp2.copy(frame.origin).sub(earthPos).length();
    const earthAngle = Math.asin(Math.min(1, (R * (1 + 12 / 6371)) / dist));
    const fromNadir = Math.acos(Math.max(-1, Math.min(1, -sunLocal.z)));
    const visible = smooth(fromNadir, earthAngle - 0.3 * DEG, earthAngle + 0.3 * DEG);
    const low = 1 - smooth(fromNadir, earthAngle, earthAngle + 3 * DEG);
    // The windows the Sun can shine through: a side window's well passes
    // rays up to about 78 degrees off its axis (none climbs more than about
    // 28 degrees above the horizontal), the top window's up to about 60
    // degrees off the nadir. With none open the shader's test is skipped.
    let canEnter = 0;
    for (let i = 0; i < 6; i++) {
      const on = sunLocal.dot(u.uWinN.value[i]) > 0.2 && sunLocal.z < 0.5 ? 1 : 0;
      u.uWinOn.value[i] = on;
      canEnter |= on;
    }
    u.uWinOn.value[6] = sunLocal.z < -0.45 ? 1 : 0;
    canEnter |= u.uWinOn.value[6];
    colTmp.copy(SUN_WHITE).lerp(SUN_LOW, low).multiplyScalar(SUN_IRRADIANCE * visible * (1 - 0.55 * low) * canEnter);
    u.uSunE.value.set(colTmp.r, colTmp.g, colTmp.b);
    // The Earth below: the Sun's height over the ground under the station,
    // softened over the 20 degrees of the Earth it sees, and the share of
    // the lower half of the sky the windows open.
    const mu0 = sunLocal.z;
    const lit = 0.5 * (mu0 + Math.sqrt(mu0 * mu0 + 0.012));
    const radiance = (EARTH_ALBEDO * SUN_IRRADIANCE / Math.PI) * lit + 0.0015;
    colTmp.copy(EARTH_DAY).multiplyScalar(radiance * Math.PI * WINDOW_SHARE);
    u.uEarthE.value.set(colTmp.r, colTmp.g, colTmp.b);
    colTmp.copy(EARTH_DAY).multiplyScalar(radiance * Math.PI * WINDOW_SHARE * BOUNCE);
    tmp.set(colTmp.r, colTmp.g, colTmp.b);
    const night = 1 - smooth(lit, 0.03, 0.4);
    u.uAdapt.value = model === 'ready' ? MODEL_ADAPT : 1;
    colTmp.copy(CABIN).multiplyScalar(CABIN_DAY + CABIN_NIGHT * night);
    u.uCabinE.value.set(colTmp.r, colTmp.g, colTmp.b).add(tmp);
    colTmp.copy(CABIN).multiplyScalar(LAMP_DAY + LAMP_NIGHT * night);
    u.uLampI.value.set(colTmp.r, colTmp.g, colTmp.b);
    colTmp.copy(EARTH_DAY).multiplyScalar(radiance);
    backdrop.material.uniforms.uEarthL.value.set(colTmp.r, colTmp.g, colTmp.b);
  }

  // ------------------------------------------------------------ placement
  function placeInside(delta) {
    const k = 1 - Math.exp(-delta * 16);
    const kh = 1 - Math.exp(-delta * 7);
    look.headT = THREE.MathUtils.clamp(look.headT, HEAD_MIN, HEAD_MAX);
    look.head += (look.headT - look.head) * kh;
    look.pitchT = THREE.MathUtils.clamp(look.pitchT, -90 * DEG, cupolaPitchLimit(look.head));
    look.pitch += (look.pitchT - look.pitch) * k;
    look.yaw += (look.yawT - look.yaw) * k;
    cupolaEye(eye, look.yaw, look.pitch, look.head);
    keepClear(eye);
    lookQuaternion(qLook);
    const camera = api.getCamera();
    const fov = cupolaFov(camera.aspect);
    if (Math.abs(camera.fov - fov) > 1e-6) { camera.fov = fov; camera.updateProjectionMatrix(); }
    toWorld(eye, camera.position);
    camera.quaternion.copy(frame.q).multiply(qLook);
    camera.up.copy(frame.z);
    camera.updateMatrixWorld(true);
    const controls = api.getControls();
    controls.enabled = false;
    gazeLocal(tmp).applyQuaternion(frame.q);
    controls.target.copy(camera.position).addScaledVector(tmp, frame.metre * 2);
    // The interior camera: the same lens, its own depth range.
    cam.position.copy(eye);
    cam.quaternion.copy(qLook);
    cam.fov = camera.fov; cam.aspect = camera.aspect; cam.zoom = camera.zoom;
    cam.projectionMatrix.copy(camera.projectionMatrix);
    const p = cam.projectionMatrix.elements;
    p[10] = -(cam.far + cam.near) / (cam.far - cam.near);
    p[14] = -2 * cam.far * cam.near / (cam.far - cam.near);
    cam.projectionMatrixInverse.copy(cam.projectionMatrix).invert();
    cam.updateMatrixWorld(true);
    sunWindowVis = sunThroughWindow(eye, sunLocal);
    updateCube(delta, eye);
  }

  // The reflection cube, a few times a second (in a headset every few
  // seconds: six more renders a frame it cannot afford, and the light it
  // holds changes slowly), the glass and the inside's own reflections off
  // while it draws.
  function updateCube(delta, at) {
    const renderer = api.getRenderer();
    cubeAge += delta;
    if (cubeAge < (renderer.xr.isPresenting ? 4 : 0.25)) return;
    cubeAge = 0;
    const autoClear = renderer.autoClear;
    renderer.autoClear = true;
    cubeCamera.position.copy(at);
    cubeCamera.updateMatrixWorld(true);
    built.glass.visible = false;
    SHARED_UNIFORMS.uEnvOn.value = 0;
    SHARED_UNIFORMS.uEnvCube.value = null;
    try {
      cubeCamera.update(renderer, scene);
    } finally {
      built.glass.visible = true;
      renderer.autoClear = autoClear;
      SHARED_UNIFORMS.uEnvCube.value = cubeTarget.texture;
      SHARED_UNIFORMS.uEnvOn.value = 1;
    }
  }

  // The framing outside, in the station's frame, so it is the same framing
  // relative to the station and the Earth whenever it comes back.
  function captureFraming() {
    const camera = api.getCamera();
    const controls = api.getControls();
    qInv.copy(frame.q).invert();
    return {
      rel: camera.position.clone().sub(frame.origin).applyQuaternion(qInv),
      target: controls.target.clone().sub(frame.origin).applyQuaternion(qInv),
      up: camera.up.clone().applyQuaternion(qInv),
    };
  }
  function placeFraming(f, scale) {
    const camera = api.getCamera();
    const controls = api.getControls();
    camera.position.copy(f.rel).multiplyScalar(scale).applyQuaternion(frame.q).add(frame.origin);
    controls.target.copy(f.target).multiplyScalar(scale).applyQuaternion(frame.q).add(frame.origin);
    camera.up.copy(f.up).applyQuaternion(frame.q);
    camera.lookAt(controls.target);
    camera.updateMatrixWorld(true);
  }

  // ------------------------------------------------------------- the modes
  function canEnter() {
    return phase === 'off' && api.isIssFocused() && !api.blockedBy() && api.isRealistic();
  }

  function enter() {
    if (phase !== 'off') return false;
    if (!api.isIssFocused() || api.blockedBy()) return false;
    // The station is only shown at true scale; focusing it switches there,
    // so this only waits for a switch that is still landing.
    if (!api.isRealistic()) {
      api.setRealistic(true);
      const started = performance.now();
      clearInterval(pendingEnter);
      pendingEnter = setInterval(() => {
        if (canEnter()) { clearInterval(pendingEnter); enter(); } else if (performance.now() - started > 8000) clearInterval(pendingEnter);
      }, 250);
      return false;
    }
    if (!computeFrame()) return false;
    ensureScene();
    ensureUi();
    const camera = api.getCamera();
    const controls = api.getControls();
    saved = {
      framing: captureFraming(),
      fov: camera.fov,
      controlsEnabled: controls.enabled,
      issVisible: api.getIss().visible,
      orbitLine: null,
    };
    const orbitLine = api.getIss().parent?.children.find((c) => c.isLine && c.name === 'ISS orbit');
    if (orbitLine) saved.orbitLine = { line: orbitLine, mask: orbitLine.layers.mask };
    controls.enabled = false;
    look.yaw = look.yawT = 0;
    look.pitch = look.pitchT = -90 * DEG;
    look.head = look.headT = HEAD_DEFAULT;
    phase = 'entering';
    phaseTime = 0;
    document.body.classList.add('cupola-mode');
    if (cardButton) cardButton.disabled = true;
    modeEnter('cupola');
    return true;
  }

  // Inside proper: the station's model and orbit line out of the way, the
  // interior pass on, the lens widened.
  function goInside() {
    const iss = api.getIss();
    iss.visible = false;
    if (saved.orbitLine) saved.orbitLine.line.layers.mask = 0;
    attachPass();
    pass.enabled = true;
    cubeAge = Infinity;
    ui.hidden = false;
    ui.classList.remove('is-idle');
    lastInput = performance.now();
    updateCaption(true);
    const touch = matchMedia('(hover: none)').matches;
    hintEl.textContent = touch ? 'Drag to look around · Pinch to lean in or out' : 'Drag to look around · Scroll to lean in or out · Esc to leave';
    hintEl.classList.add('on');
    clearTimeout(hintTimer);
    hintTimer = setTimeout(() => hintEl.classList.remove('on'), 5500);
    phase = 'inside';
    phaseTime = 0;
  }

  // Back out: everything put back as it was found, without moving the
  // camera (another mode taking over places it itself).
  function restore() {
    const camera = api.getCamera();
    const controls = api.getControls();
    const iss = api.getIss();
    if (pass) pass.enabled = false;
    if (saved) {
      if (iss) iss.visible = saved.issVisible;
      if (saved.orbitLine) saved.orbitLine.line.layers.mask = saved.orbitLine.mask;
      if (Math.abs(camera.fov - saved.fov) > 1e-6) { camera.fov = saved.fov; camera.updateProjectionMatrix(); }
      controls.enabled = saved.controlsEnabled;
    }
    sunWindowVis = 1;
    if (ui) ui.hidden = true;
    if (hintEl) hintEl.classList.remove('on');
    clearTimeout(hintTimer);
    if (fadeEl) fadeEl.style.opacity = '0';
    document.body.classList.remove('cupola-mode');
  }

  function leave() {
    if (phase === 'inside') {
      phase = 'leaving-out';
      phaseTime = 0;
    } else if (phase === 'entering') {
      // Turned back on the way in: straight back out.
      phase = 'leaving-in';
      phaseTime = LEAVE_DOLLY_S * (1 - Math.min(1, phaseTime / ENTER_S));
    }
  }

  // Leaves at once, without the fade: another mode took over, or the station
  // is no longer the focus.
  function abort() {
    if (phase === 'off') return;
    restore();
    phase = 'off';
    saved = null;
    modeExit('cupola');
  }

  function finish() {
    restore();
    phase = 'off';
    saved = null;
    modeExit('cupola');
  }

  // --------------------------------------------------------------- the HUD
  let where = null;
  let water = null;
  let captionAt = 0;
  function loadWhere() {
    if (where) return;
    where = {};
    import('../site/iss/where.js').then((m) => {
      where.whereIs = m.whereIs;
      water = m.waterMask('/marketing/flat-earth/water-2k.webp');
    }).catch(() => {});
  }
  function updateCaption(force = false) {
    const now = performance.now();
    if (!force && now - captionAt < 2000) return;
    captionAt = now;
    loadWhere();
    const satrec = api.getSatrec();
    const st = satrec ? issState(satrec, api.getSimDate()) : null;
    let text;
    if (!st) text = 'Low Earth orbit, about 420 km up';
    else {
      const isWater = water ? water.isWater(st.lat, st.lon) : null;
      const place = where && where.whereIs && isWater !== null
        ? where.whereIs(st.lat, st.lon, isWater).text
        : `Over ${Math.abs(st.lat).toFixed(1)}° ${st.lat >= 0 ? 'N' : 'S'}, ${Math.abs(st.lon).toFixed(1)}° ${st.lon >= 0 ? 'E' : 'W'}`;
      text = `${place} · ${st.sunlit ? 'in sunlight' : 'in the Earth’s shadow'}`;
    }
    if (text !== caption) {
      caption = text;
      captionEl.textContent = text;
    }
  }

  // --------------------------------------------------------------- input
  const pointers = new Map();
  let pinchFrom = 0;
  function canvasEvent(e) {
    const r = api.getRenderer();
    return r && e.target === r.domElement;
  }
  function noteInput() {
    lastInput = performance.now();
    if (ui) ui.classList.remove('is-idle');
  }
  const stopAll = (e) => {
    if (phase === 'off' || !canvasEvent(e)) return;
    e.stopImmediatePropagation();
    if (e.cancelable && e.type !== 'pointermove' && e.type !== 'mousemove') e.preventDefault();
  };
  window.addEventListener('pointerdown', (e) => {
    if (phase === 'off' || !canvasEvent(e)) return;
    e.stopImmediatePropagation();
    if (e.pointerType === 'mouse' && e.button !== 0) return;
    pointers.set(e.pointerId, { x: e.clientX, y: e.clientY });
    try { e.target.setPointerCapture(e.pointerId); } catch { /* already gone */ }
    if (pointers.size === 2) {
      const [a, b] = [...pointers.values()];
      pinchFrom = Math.hypot(a.x - b.x, a.y - b.y);
    }
    noteInput();
  }, true);
  window.addEventListener('pointermove', (e) => {
    if (phase === 'off') return;
    const p = pointers.get(e.pointerId);
    if (canvasEvent(e)) e.stopImmediatePropagation();
    if (!p) { if (canvasEvent(e)) noteInput(); return; }
    const dx = e.clientX - p.x, dy = e.clientY - p.y;
    p.x = e.clientX; p.y = e.clientY;
    if (phase !== 'inside') return;
    if (pointers.size >= 2) {
      const [a, b] = [...pointers.values()];
      const d = Math.hypot(a.x - b.x, a.y - b.y);
      if (pinchFrom > 0) look.headT -= (d - pinchFrom) * 0.0018;
      pinchFrom = d;
    } else {
      // The view follows the finger: a drag moves the scene with it.
      const perPixel = (api.getCamera().fov * DEG) / Math.max(1, window.innerHeight);
      look.yawT += dx * perPixel;
      look.pitchT += dy * perPixel;
    }
    noteInput();
  }, true);
  const endPointer = (e) => {
    if (!pointers.has(e.pointerId)) return;
    pointers.delete(e.pointerId);
    pinchFrom = 0;
    if (canvasEvent(e)) e.stopImmediatePropagation();
  };
  window.addEventListener('pointerup', endPointer, true);
  window.addEventListener('pointercancel', endPointer, true);
  for (const type of ['click', 'dblclick', 'mousedown', 'mouseup', 'mousemove', 'contextmenu', 'auxclick', 'touchstart', 'touchmove', 'touchend']) {
    window.addEventListener(type, (e) => {
      if (type === 'dblclick' && phase === 'inside' && canvasEvent(e)) {
        look.yawT = look.yaw; look.pitchT = -90 * DEG;
      }
      stopAll(e);
    }, { capture: true, passive: false });
  }
  window.addEventListener('wheel', (e) => {
    if (phase === 'off' || !canvasEvent(e)) return;
    e.stopImmediatePropagation();
    e.preventDefault();
    if (phase !== 'inside') return;
    let dy = e.deltaY;
    if (e.deltaMode === 1) dy *= 16;
    else if (e.deltaMode === 2) dy *= 100;
    look.headT += THREE.MathUtils.clamp(dy, -120, 120) * 0.0009;
    noteInput();
  }, { capture: true, passive: false });
  window.addEventListener('keydown', (e) => {
    if (e.metaKey || e.ctrlKey || e.altKey) return;
    const t = e.target;
    if (t && (t.isContentEditable || /^(INPUT|TEXTAREA|SELECT)$/.test(t.tagName))) return;
    if (document.querySelector('dialog[open]')) return;
    if (e.code === 'KeyC' && !e.shiftKey && !e.repeat) {
      if (phase === 'inside' || phase === 'entering') { leave(); e.preventDefault(); return; }
      if (phase === 'off' && api.isIssFocused() && !api.blockedBy()) { enter(); e.preventDefault(); }
      return;
    }
    if (phase === 'off') return;
    if (e.key === 'Escape') { leave(); e.preventDefault(); e.stopImmediatePropagation(); return; }
    if (phase !== 'inside') return;
    const step = 3 * DEG;
    if (e.key === 'ArrowLeft') look.yawT += step;
    else if (e.key === 'ArrowRight') look.yawT -= step;
    else if (e.key === 'ArrowUp') look.pitchT += step;
    else if (e.key === 'ArrowDown') look.pitchT -= step;
    else return;
    e.preventDefault();
    noteInput();
  }, true);

  // The card's button follows whether the Cupola can be entered now.
  mountCardButton();
  setInterval(() => {
    if (!cardButton) mountCardButton();
    if (cardButton && phase === 'off') {
      const ok = canEnter();
      if (cardButton.disabled === ok) cardButton.disabled = !ok;
    }
  }, 400);

  // --------------------------------------------------------- every frame
  // Called by main.js once the bodies have moved and the follow code has
  // placed its camera, before the frame renders.
  function update(delta) {
    if (phase === 'off') return;
    const dt = Math.min(Math.max(delta || 0, 0), 0.1);
    phaseTime += dt;
    if (api.blockedBy() || !api.isRealistic() || !api.isIssFocused() || !computeFrame()) { abort(); return; }
    attachPass();
    wrapGlare();
    updateLight();
    if (phase === 'entering') {
      // Down the line to the station, into black.
      const k = Math.min(1, phaseTime / ENTER_S);
      placeFraming(saved.framing, Math.exp(Math.log(0.04) * smooth(k, 0, 1)));
      api.getControls().enabled = false;
      fadeEl.style.opacity = String(smooth(k, 0.3, 0.95));
      if (k >= 1 && (model !== 'loading' || phaseTime > ENTER_S + 8)) goInside();
      return;
    }
    if (phase === 'inside') {
      placeInside(dt);
      fadeEl.style.opacity = String(1 - smooth(phaseTime, BLACK_HOLD_S, BLACK_HOLD_S + FADE_IN_S));
      updateCaption();
      if (performance.now() - lastInput > 6000 && !ui.classList.contains('is-idle')) ui.classList.add('is-idle');
      return;
    }
    if (phase === 'leaving-out') {
      placeInside(dt);
      const k = Math.min(1, phaseTime / LEAVE_FADE_S);
      fadeEl.style.opacity = String(smooth(k, 0, 1));
      if (k >= 1) {
        // Out of the station in black, then back along the old framing.
        const iss = api.getIss();
        pass.enabled = false;
        iss.visible = saved.issVisible;
        if (saved.orbitLine) saved.orbitLine.line.layers.mask = saved.orbitLine.mask;
        const camera = api.getCamera();
        if (Math.abs(camera.fov - saved.fov) > 1e-6) { camera.fov = saved.fov; camera.updateProjectionMatrix(); }
        sunWindowVis = 1;
        ui.hidden = true;
        phase = 'leaving-in';
        phaseTime = 0;
      }
      return;
    }
    if (phase === 'leaving-in') {
      const k = Math.min(1, phaseTime / LEAVE_DOLLY_S);
      if (pass.enabled) {
        pass.enabled = false;
        api.getIss().visible = saved.issVisible;
        const camera = api.getCamera();
        if (Math.abs(camera.fov - saved.fov) > 1e-6) { camera.fov = saved.fov; camera.updateProjectionMatrix(); }
      }
      placeFraming(saved.framing, Math.exp(Math.log(0.06) * (1 - smooth(k, 0, 1))));
      api.getControls().enabled = false;
      fadeEl.style.opacity = String(1 - smooth(k, 0, 0.5));
      if (k >= 1) {
        placeFraming(saved.framing, 1);
        finish();
      }
    }
  }

  const view = {
    update,
    enter,
    leave,
    toggle: () => (phase === 'off' ? enter() : (leave(), true)),
    get active() { return phase !== 'off'; },
    get phase() { return phase; },
    // The station frame and the inside, for the headset (src/xr/experience.js):
    // the head stands 75 cm over the top window, up away from the Earth,
    // facing the port window (the workstation's laptops fill the forward
    // side).
    get xr() {
      if (!built || !scene || !computeFrame()) return null;
      return { scene, origin: frame.origin.clone(), quaternion: frame.q.clone(), metre: frame.metre, up: frame.z.clone(), forward: frame.y.clone(), eye: toWorld(new THREE.Vector3(0, 0, 0.75), new THREE.Vector3()) };
    },
  };

  // For checks from the console or a test.
  window.__cupola = {
    enter, leave, abort,
    get active() { return phase !== 'off'; },
    get phase() { return phase; },
    get canEnter() { return canEnter(); },
    get caption() { return caption; },
    get satrec() { return api.getSatrec(); },
    // Degrees; the clamp applies as in a drag.
    look(yawDeg, pitchDeg) { look.yawT = yawDeg * DEG; look.pitchT = pitchDeg * DEG; },
    lean(head) { look.headT = head; },
    snap() { look.yaw = look.yawT; look.pitch = look.pitchT; look.head = look.headT; },
    state() {
      const camera = api.getCamera();
      const rel = camera.position.clone().sub(frame.origin).applyQuaternion(qInv.copy(frame.q).invert()).divideScalar(frame.metre || 1);
      return {
        phase, yaw: look.yaw / DEG, pitch: look.pitch / DEG, head: look.head, pitchLimit: cupolaPitchLimit(look.head) / DEG,
        eye: eye.toArray(), cameraInStationMetres: rel.toArray(), fov: camera.fov, sunWindowVis,
        sunLocal: sunLocal.toArray(), sunE: SHARED_UNIFORMS.uSunE.value.toArray(), earthE: SHARED_UNIFORMS.uEarthE.value.toArray(),
        passIndex: attachedComposer ? attachedComposer.passes.indexOf(pass) : -1,
      };
    },
    get bounds() { return built ? built.bounds.clone() : null; },
    get model() { return model; },
  };
  return view;
}
